import React, { useState, useEffect, useCallback } from 'react';
import {
  Composite,
  Surcharge,
  FamilyLookupItem, // NEW: Import FamilyLookupItem
  SubFamilyLookupItem, // NEW: Import SubFamilyLookupItem
  BarcodeType1LookupItem, // NEW: Import BarcodeType1LookupItem
  BarcodeType2LookupItem, // NEW: Import BarcodeType2LookupItem
} from '../types/costwand.d';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Input } from './ui/input.tsx';
import { Spinner } from './ui/spinner.tsx';

interface CompositeFormProps {
  initialData: Composite;
  onSubmit: (data: {
    description: string; // NEW: Add description
    family_id: number; // NEW: Add family_id
    subfamily_id: number | null; // NEW: Add subfamily_id
    barcode_1_id: number | null; // NEW: Add barcode_1_id
    barcode_2_id: number | null; // NEW: Add barcode_2_id
    tax_profile_id: number;
    markup_perc_wholesale: number;
    markup_perc_retail: number;
  }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  surcharges: Surcharge[];
  families: FamilyLookupItem[]; // NEW: Add families lookup
  subFamilies: SubFamilyLookupItem[]; // NEW: Add subFamilies lookup
  barcodeType1s: BarcodeType1LookupItem[]; // NEW: Add barcodeType1s lookup
  barcodeType2s: BarcodeType2LookupItem[]; // NEW: Add barcodeType2s lookup
  lookupLoading: boolean;
}

const CompositeForm: React.FC<CompositeFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  surcharges,
  families, // NEW
  subFamilies, // NEW
  barcodeType1s, // NEW
  barcodeType2s, // NEW
  lookupLoading,
}) => {
  const [description, setDescription] = useState<string>(initialData.description); // NEW
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(initialData.family_id || null); // NEW
  const [selectedSubFamilyId, setSelectedSubFamilyId] = useState<number | null>(initialData.subfamily_id || null); // NEW
  const [selectedBarcode1Id, setSelectedBarcode1Id] = useState<number | null>(initialData.barcode_1_id || null); // NEW
  const [selectedBarcode2Id, setSelectedBarcode2Id] = useState<number | null>(initialData.barcode_2_id || null); // NEW
  const [selectedSurchargeId, setSelectedSurchargeId] = useState<number | null>(
    initialData.tax_profile_id || null
  );
  const [wholesaleMarkup, setWholesaleMarkup] = useState<number>(initialData.markup_perc_wholesale);
  const [retailMarkup, setRetailMarkup] = useState<number>(initialData.markup_perc_retail);

  useEffect(() => {
    setDescription(initialData.description); // NEW
    setSelectedFamilyId(initialData.family_id || null); // NEW
    setSelectedSubFamilyId(initialData.subfamily_id || null); // NEW
    setSelectedBarcode1Id(initialData.barcode_1_id || null); // NEW
    setSelectedBarcode2Id(initialData.barcode_2_id || null); // NEW
    setSelectedSurchargeId(initialData.tax_profile_id || null);
    setWholesaleMarkup(initialData.markup_perc_wholesale);
    setRetailMarkup(initialData.markup_perc_retail);
  }, [initialData]);

  // NEW: Handle change for description input
  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  // NEW: Generic handler for select changes (family, subfamily, barcode types, surcharge)
  const handleSelectChange = useCallback((field: string, value: string) => {
    const parsedValue = value === '' || value === '-' ? null : parseInt(value, 10); // Handle "None" option
    if (field === 'family_id') setSelectedFamilyId(parsedValue);
    else if (field === 'subfamily_id') setSelectedSubFamilyId(parsedValue);
    else if (field === 'barcode_1_id') setSelectedBarcode1Id(parsedValue);
    else if (field === 'barcode_2_id') setSelectedBarcode2Id(parsedValue);
    else if (field === 'tax_profile_id') setSelectedSurchargeId(parsedValue);
  }, []);

  const handleMarkupChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const parsedValue = parseFloat(value) || 0;
    if (id === 'markup_perc_wholesale') {
      setWholesaleMarkup(parsedValue);
    } else if (id === 'markup_perc_retail') {
      setRetailMarkup(parsedValue);
    }
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Ensure required fields are not null before submitting
      if (selectedFamilyId !== null && selectedSurchargeId !== null) {
        onSubmit({
          description: description,
          family_id: selectedFamilyId,
          subfamily_id: selectedSubFamilyId,
          barcode_1_id: selectedBarcode1Id,
          barcode_2_id: selectedBarcode2Id,
          tax_profile_id: selectedSurchargeId,
          markup_perc_wholesale: wholesaleMarkup,
          markup_perc_retail: retailMarkup,
        });
      }
    },
    [
      description,
      selectedFamilyId,
      selectedSubFamilyId,
      selectedBarcode1Id,
      selectedBarcode2Id,
      selectedSurchargeId,
      wholesaleMarkup,
      retailMarkup,
      onSubmit,
    ]
  );

  const isDisabled = isSubmitting || lookupLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Editable field: Description */}
      <div>
        <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">Description</Label>
        <Input
          id="description"
          type="text"
          required
          value={description}
          onChange={handleDescriptionChange}
          disabled={isDisabled}
        />
      </div>

      {/* Display non-editable fields */}
      <div>
        <Label className="block text-sm font-medium text-foreground mb-1">Article Number</Label>
        <p className="text-muted-foreground text-sm">{initialData.article_number}</p>
      </div>
      <div>
        <Label className="block text-sm font-medium text-foreground mb-1">Current Cost</Label>
        <p className="text-muted-foreground text-sm">{initialData.cost?.toFixed(4) || 'N/A'}</p>
      </div>

      {/* NEW: Editable field: Family */}
      <div>
        <Label htmlFor="family_id" className="block text-sm font-medium text-foreground mb-1">
          Family
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('family_id', value)}
          value={selectedFamilyId ? String(selectedFamilyId) : ''}
          disabled={isDisabled}
          required
        >
          <SelectTrigger id="family_id">
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading families...</span> : 'Select a family'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nil" disabled>
              {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading families...</span> : 'Select a family'}
            </SelectItem>
            {families.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* NEW: Editable field: Subfamily */}
      <div>
        <Label htmlFor="subfamily_id" className="block text-sm font-medium text-foreground mb-1">
          Subfamily (Optional)
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('subfamily_id', value)}
          value={selectedSubFamilyId ? String(selectedSubFamilyId) : ''}
          disabled={isDisabled}
        >
          <SelectTrigger id="subfamily_id">
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading subfamilies...</span> : 'Select a subfamily (optional)'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-">None</SelectItem>
            {subFamilies.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* NEW: Editable field: Barcode Type 1 */}
      <div>
        <Label htmlFor="barcode_1_id" className="block text-sm font-medium text-foreground mb-1">
          Barcode Type 1 (Optional)
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('barcode_1_id', value)}
          value={selectedBarcode1Id ? String(selectedBarcode1Id) : ''}
          disabled={isDisabled}
        >
          <SelectTrigger id="barcode_1_id">
            <SelectValue
              placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading barcode types...</span> : 'Select Barcode Type 1 (optional)'}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-">None</SelectItem>
            {barcodeType1s.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* NEW: Editable field: Barcode Type 2 */}
      <div>
        <Label htmlFor="barcode_2_id" className="block text-sm font-medium text-foreground mb-1">
          Barcode Type 2 (Optional)
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('barcode_2_id', value)}
          value={selectedBarcode2Id ? String(selectedBarcode2Id) : ''}
          disabled={isDisabled}
        >
          <SelectTrigger id="barcode_2_id">
            <SelectValue
              placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading barcode types...</span> : 'Select Barcode Type 2 (optional)'}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="-">None</SelectItem>
            {barcodeType2s.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Editable field: Surcharge Profile */}
      <div>
        <Label htmlFor="tax_profile_id" className="block text-sm font-medium text-foreground mb-1">
          Surcharge Profile
        </Label>
        <Select
          onValueChange={(value) => handleSelectChange('tax_profile_id', value)}
          value={selectedSurchargeId ? String(selectedSurchargeId) : ''}
          disabled={isDisabled}
          required
        >
          <SelectTrigger id="tax_profile_id">
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading surcharge profiles...</span> : 'Select a surcharge profile'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nil" disabled>
              {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading surcharge profiles...</span> : 'Select a surcharge profile'}
            </SelectItem>
            {surcharges.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Editable field: Wholesale Markup */}
      <div>
        <Label htmlFor="markup_perc_wholesale" className="block text-sm font-medium text-foreground mb-1">
          Wholesale Markup %
        </Label>
        <Input
          id="markup_perc_wholesale"
          type="number"
          step="0.01"
          required
          value={wholesaleMarkup}
          onChange={handleMarkupChange}
          disabled={isDisabled}
        />
      </div>

      {/* Editable field: Retail Markup */}
      <div>
        <Label htmlFor="markup_perc_retail" className="block text-sm font-medium text-foreground mb-1">
          Retail Markup %
        </Label>
        <Input
          id="markup_perc_retail"
          type="number"
          step="0.01"
          required
          value={retailMarkup}
          onChange={handleMarkupChange}
          disabled={isDisabled}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isDisabled || selectedSurchargeId === null || selectedFamilyId === null}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CompositeForm;